// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import api from '../../../api/client';
import { getActiveTenant } from '../../../helpers/tenantHelpers';
import { FormRow, SaveBar, Section, SectionRail } from './primitives';

/* Customer Portal — on the approved shell. One save bar; uploads are
   object actions that mark the page dirty until saved. */

const SECTIONS = [
  { id: 'portal', label: 'Portal' },
  { id: 'brand', label: 'Branding' },
  { id: 'domain', label: 'Custom domain' },
  { id: 'codes', label: 'Sign-in codes' },
];

const PORTAL_ROOT = 'portal.abon.ai';

function slugifyTenant(t) {
  const base = String(t?.domain || t?.name || '').toLowerCase();
  return base
    .replace(/^https?:\/\//, '')
    .replace(/\..*$/, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'your-business';
}

export default function PortalTab() {
  const tenant = getActiveTenant() || {};
  const slug = slugifyTenant(tenant);
  const portalUrl = `https://${slug}.${PORTAL_ROOT}`;

  const [twilio, setTwilio] = useState(null);
  const [prefsSms, setPrefsSms] = useState(false);
  const [form, setForm] = useState(null);
  const [baseline, setBaseline] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingKey, setUploadingKey] = useState('');
  const logoRef = useRef(null);
  const bgRef = useRef(null);

  const dirty = useMemo(
    () => form && JSON.stringify(form) !== JSON.stringify(baseline),
    [form, baseline]
  );

  const load = useCallback(async () => {
    const [portalRes, prefsRes, twilioRes] = await Promise.allSettled([
      api.get('/accounts/portal-settings/'),
      api.get('/accounts/preferences/'),
      api.get('/accounts/twilio-settings/'),
    ]);
    if (portalRes.status === 'fulfilled') {
      const p0 = portalRes.value.data || {};
      const custom = prefsRes.status === 'fulfilled'
        ? (prefsRes.value.data?.preferences?.portal?.custom_domain || '')
        : '';
      const seed = {
        enabled: !!p0.portal_enabled,
        logo_url: p0.logo_url || '',
        background_image_url: p0.background_image_url || '',
        tagline: p0.tagline || '',
        custom_domain: custom,
      };
      setForm(seed);
      setBaseline(seed);
    } else {
      toast.error('Could not load portal settings');
    }
    if (prefsRes.status === 'fulfilled') {
      setPrefsSms(!!prefsRes.value.data?.preferences?.sms?.a2p_approved);
    }
    if (twilioRes.status === 'fulfilled') setTwilio(twilioRes.value.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch('/accounts/portal-settings/', {
        portal_enabled: form.enabled,
        logo_url: form.logo_url,
        background_image_url: form.background_image_url,
        tagline: form.tagline,
      });
      await api.patch('/accounts/preferences/', {
        preferences: { portal: { custom_domain: form.custom_domain } },
      });
      setBaseline({ ...form });
      toast.success('Portal settings saved');
    } catch {
      toast.error('Could not save portal settings');
    } finally {
      setSaving(false);
    }
  };

  const upload = async (file, key) => {
    if (!file) return;
    setUploadingKey(key);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('category', 'portal');
      const { data } = await api.post('/storage/files/upload/', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const row = data?.file || data || {};
      const url = row.url || row.file_url || '';
      if (!url) throw new Error('no url');
      setForm((p) => ({ ...p, [key]: url }));
      toast.success('Uploaded — save to apply');
    } catch {
      toast.error('Upload failed');
    } finally {
      setUploadingKey('');
    }
  };

  if (!form) {
    return (
      <div className="sv2-body"><div className="sv2-col">
        <p className="sv2-section-sub">Loading portal settings…</p>
      </div></div>
    );
  }

  const smsNumber = twilio?.default_number || twilio?.numbers?.[0]?.phone_number || '';
  const smsReady = !!(prefsSms && smsNumber);

  return (
    <div className="sv2-body">
      <SectionRail sections={SECTIONS} />
      <div className="sv2-col">
        <Section id="portal" title="Portal"
          sub="A passwordless site where customers see appointments, quotes, invoices and messages.">
          <FormRow label="Portal" help="Customers can sign in while this is on.">
            <div className="sv2-value" style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10 }}>
              <span className={`sv2-pill ${form.enabled ? 'ok' : 'off'}`}>{form.enabled ? 'On' : 'Off'}</span>
              <input type="checkbox" checked={form.enabled} aria-label="Portal enabled"
                onChange={(e) => setForm((p) => ({ ...p, enabled: e.target.checked }))}
                style={{ width: 18, height: 18 }} />
            </div>
          </FormRow>
          <FormRow label="Portal address" value={portalUrl} mono
            help="Share this with customers — it's live whenever the portal is on." />
        </Section>

        <Section id="brand" title="Branding"
          sub="What customers see on the sign-in screen.">
          <FormRow label="Logo" help="Shown at the top of the portal.">
            <div style={{ display: 'grid', gap: 6, justifyItems: 'end' }}>
              {form.logo_url ? (
                <img src={form.logo_url} alt="Portal logo"
                  style={{ maxHeight: 44, maxWidth: '100%', borderRadius: 8, border: '1px solid var(--border-1)', objectFit: 'contain', background: 'var(--bg-2)', padding: 4 }} />
              ) : null}
              <div className="sv2-actions">
                <button className="sv2-btn sm" disabled={uploadingKey === 'logo_url'}
                  onClick={() => logoRef.current?.click()}>
                  {uploadingKey === 'logo_url' ? 'Uploading…' : form.logo_url ? 'Replace' : 'Upload logo'}
                </button>
                {form.logo_url && (
                  <button className="sv2-btn sm" onClick={() => setForm((p) => ({ ...p, logo_url: '' }))}>Remove</button>
                )}
              </div>
              <input ref={logoRef} type="file" accept="image/*" style={{ display: 'none' }}
                onChange={(e) => { upload(e.target.files?.[0], 'logo_url'); e.target.value = ''; }} />
            </div>
          </FormRow>
          <FormRow label="Background photo" help="Behind the sign-in card — a wide photo of your work looks best.">
            <div style={{ display: 'grid', gap: 6, justifyItems: 'end' }}>
              {form.background_image_url ? (
                <img src={form.background_image_url} alt="Portal background"
                  style={{ maxHeight: 44, width: '100%', borderRadius: 8, border: '1px solid var(--border-1)', objectFit: 'cover' }} />
              ) : null}
              <div className="sv2-actions">
                <button className="sv2-btn sm" disabled={uploadingKey === 'background_image_url'}
                  onClick={() => bgRef.current?.click()}>
                  {uploadingKey === 'background_image_url' ? 'Uploading…' : form.background_image_url ? 'Replace' : 'Upload photo'}
                </button>
                {form.background_image_url && (
                  <button className="sv2-btn sm" onClick={() => setForm((p) => ({ ...p, background_image_url: '' }))}>Remove</button>
                )}
              </div>
              <input ref={bgRef} type="file" accept="image/*" style={{ display: 'none' }}
                onChange={(e) => { upload(e.target.files?.[0], 'background_image_url'); e.target.value = ''; }} />
            </div>
          </FormRow>
          <FormRow label="Tagline" help="One line under your logo on the sign-in screen.">
            <input type="text" maxLength={200} value={form.tagline}
              placeholder='e.g. "Making your day unforgettable"'
              onChange={(e) => setForm((p) => ({ ...p, tagline: e.target.value }))} />
          </FormRow>
        </Section>

        <Section id="domain" title="Custom domain"
          sub={`Optional — serve the portal from your own domain instead of ${PORTAL_ROOT}.`}>
          <FormRow label="Domain" help={`Leave blank to keep ${portalUrl}.`}>
            <input type="text" value={form.custom_domain} placeholder="portal.yourdomain.com"
              onChange={(e) => setForm((p) => ({ ...p, custom_domain: e.target.value.trim().toLowerCase() }))} />
          </FormRow>
          {form.custom_domain ? (
            <FormRow label="One DNS step" help="Add this record with your domain provider — that's all.">
              <div className="sv2-value mono">{form.custom_domain} → {slug}.{PORTAL_ROOT}</div>
            </FormRow>
          ) : null}
        </Section>

        <Section id="codes" title="Sign-in codes"
          sub="How customers receive their one-time sign-in code.">
          <div className="sv2-status-row">
            <span className="sv2-label">Codes sent by</span>
            <span className={`sv2-pill ${smsReady ? 'ok' : 'info'}`}>{smsReady ? 'Text message' : 'Email'}</span>
            <span className="sv2-status-copy">
              {smsReady
                ? `From ${smsNumber}`
                : 'Switches to text from your number once texting approval completes.'}
            </span>
          </div>
        </Section>
      </div>

      <SaveBar dirty={!!dirty} saving={saving} onSave={save}
        onDiscard={() => setForm(baseline)} />
    </div>
  );
}
